import { useEffect, useState } from 'react';
import { ConfirmModal } from '../components/ConfirmModal';
import { apiClient } from '../lib/api-client';

type SchedulerSessionStats = {
  success: boolean;
  activeCount: number;
  totalCount: number;
  expiringSoon: number;
};

type BulkRevokeResult = {
  success: boolean;
  message: string;
  revokedCount: number;
  affectedUserCount: number;
  affectedUserIds: string[];
};

export default function SchedulerSessionsPage() {
  const [stats, setStats] = useState<SchedulerSessionStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    beforeDate?: Date;
  }>({
    isOpen: false,
    beforeDate: undefined
  });

  useEffect(() => {
    void loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setError('');
      const data = await apiClient.getSchedulerSessionStats();
      setStats(data);
    } catch (err) {
      setError('Failed to load scheduler session statistics');
      console.error('Failed to load scheduler session stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkRevoke = async (beforeDate?: Date) => {
    try {
      setRevoking(true);
      setError('');
      setSuccessMessage('');

      const result: BulkRevokeResult = await apiClient.bulkRevokeSchedulerSessions(beforeDate);

      if (result.success) {
        setSuccessMessage(
          `Successfully revoked ${result.revokedCount} scheduler session(s) affecting ${result.affectedUserCount} user(s)`
        );
        // Reload stats after revocation
        await loadStats();
      } else {
        setError('Failed to revoke scheduler sessions');
      }
    } catch (err) {
      setError('Failed to revoke scheduler sessions');
      console.error('Bulk revoke failed:', err);
    } finally {
      setRevoking(false);
      closeConfirmModal();
    }
  };

  const openConfirmModal = (beforeDate?: Date) => {
    setConfirmModal({ isOpen: true, beforeDate });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ isOpen: false, beforeDate: undefined });
  };

  if (loading) {
    return (
      <div className='mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8'>
        <div className='text-center dark:text-gray-300'>Loading...</div>
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8'>
      {/* Header */}
      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>
          Scheduler Sessions Management
        </h1>
        <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
          Manage scheduler OAuth sessions for all users
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className='mb-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-800 dark:border-red-900/30 dark:bg-red-900/20 dark:text-red-200'>
          {error}
        </div>
      )}
      {successMessage && (
        <div className='mb-6 rounded border border-green-200 bg-green-50 px-4 py-3 text-green-800 dark:border-green-900/30 dark:bg-green-900/20 dark:text-green-200'>
          {successMessage}
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className='mb-8 grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-4 md:gap-6'>
          <div className='overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800'>
            <div className='px-3 py-3 md:px-4 md:py-5 lg:p-6'>
              <dt className='truncate text-xs font-medium text-gray-500 md:text-sm dark:text-gray-400'>
                Active Sessions
              </dt>
              <dd className='mt-1 text-xl font-semibold text-gray-900 md:text-3xl dark:text-white'>
                {stats.activeCount}
              </dd>
            </div>
          </div>

          <div className='overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800'>
            <div className='px-3 py-3 md:px-4 md:py-5 lg:p-6'>
              <dt className='truncate text-xs font-medium text-gray-500 md:text-sm dark:text-gray-400'>
                Total Sessions
              </dt>
              <dd className='mt-1 text-xl font-semibold text-gray-900 md:text-3xl dark:text-white'>
                {stats.totalCount}
              </dd>
            </div>
          </div>

          <div className='overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800'>
            <div className='px-3 py-3 md:px-4 md:py-5 lg:p-6'>
              <dt className='truncate text-xs font-medium text-gray-500 md:text-sm dark:text-gray-400'>
                Expiring Soon
              </dt>
              <dd className='mt-1 text-xl font-semibold text-yellow-600 md:text-3xl dark:text-yellow-400'>
                {stats.expiringSoon}
              </dd>
              <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-400'>Within 7 days</p>
            </div>
          </div>

          <div className='overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800'>
            <div className='px-3 py-3 md:px-4 md:py-5 lg:p-6'>
              <dt className='truncate text-xs font-medium text-gray-500 md:text-sm dark:text-gray-400'>
                Inactive Sessions
              </dt>
              <dd className='mt-1 text-xl font-semibold text-gray-900 md:text-3xl dark:text-white'>
                {stats.totalCount - stats.activeCount}
              </dd>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Revocation Section */}
      <div className='rounded-lg bg-white shadow dark:bg-gray-800'>
        <div className='px-4 py-5 sm:p-6'>
          <h2 className='mb-4 text-lg font-medium text-gray-900 dark:text-white'>
            Bulk Revocation
          </h2>
          <p className='mb-6 text-sm text-gray-600 dark:text-gray-400'>
            Revoke all active scheduler sessions. This will require affected users to re-authorize
            the scheduler in their settings.
          </p>

          <div className='space-y-4'>
            <div>
              <button
                type='button'
                onClick={() => openConfirmModal()}
                disabled={revoking || (stats?.activeCount ?? 0) === 0}
                className='inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600 dark:disabled:text-gray-400'
              >
                {revoking ? 'Revoking...' : 'Revoke All Active Sessions'}
              </button>
              {stats && stats.activeCount === 0 && (
                <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>
                  No active sessions to revoke
                </p>
              )}
            </div>
          </div>

          <div className='mt-6 border-t border-gray-200 pt-6 dark:border-gray-700'>
            <h3 className='mb-2 text-sm font-medium text-gray-900 dark:text-white'>⚠️ Warning</h3>
            <ul className='list-inside list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400'>
              <li>This action will immediately invalidate all active scheduler sessions</li>
              <li>
                Users will need to re-authorize the scheduler to continue using scheduled posts
              </li>
              <li>Pending scheduled posts will fail until re-authorization</li>
              <li>This action cannot be undone</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title='Revoke All Scheduler Sessions?'
        message={`This will revoke ${stats?.activeCount ?? 0} active scheduler session(s).

All users with scheduler authorization will need to re-authorize to continue using scheduled posts.

Type "REVOKE" to confirm this action.`}
        confirmText='REVOKE'
        onConfirm={() => handleBulkRevoke(confirmModal.beforeDate)}
        onCancel={closeConfirmModal}
        variant='danger'
        isLoading={revoking}
      />
    </div>
  );
}
